import { i18n } from '@core/config/i18n.installer.js'
import router from '@core/config/router.installer.js'
import { useAuthStore } from '@modules/auth/stores/auth.store.js'
import { ApiError, AuthenticationError } from '@shared/errors/custom.error.js'
import { LOGOUT_REASONS, setLogoutReason, setPostLoginRedirect } from '@shared/utils/company-cache.utils.js'
import { savePendingSubmission } from '@shared/utils/pending-submission.utils.js'
import axios from 'axios'
import ToastEventBus from 'primevue/toasteventbus'

const publicEndpoints = [
  '/token/revoke',
  '/auth/identify',
  '/token/exchange',
]

const SESSION_EXPIRED_REDIRECT_DELAY_MS = 4000

const MUTATING_METHODS = new Set(['post', 'put', 'patch', 'delete'])

let isHandlingRefreshFailure = false

export function resetRefreshFailureStateForTests() {
  isHandlingRefreshFailure = false
}

/**
 * Check if the request is targeting a public endpoint.
 */
function isPublicRequest(request) {
  return publicEndpoints.some(endpoint => request.url.includes(endpoint))
}

/**
 * Determines if the error qualifies for a token refresh.
 */
function isUnauthorizedError(error, request) {
  return (
    axios.isAxiosError(error)
    && error.response?.status === 401
    && !request._retry
  )
}

/**
 * Preserves the payload of a submit lost to session expiry so it can be
 * offered for resend after the next login. Only JSON payloads qualify:
 * multipart bodies (attachment uploads) cannot survive a page navigation.
 */
function capturePendingSubmission(request, isMutation) {
  if (!isMutation || typeof request?.data !== 'string')
    return

  const authStore = useAuthStore()
  // b2cId is the only user identifier stable across logins (user.id is
  // session-scoped), so it is what binds the entry to its owner.
  const b2cId = authStore.userData?.b2cId ?? null
  if (!b2cId)
    return

  savePendingSubmission({
    baseURL: request.baseURL ?? '',
    url: request.url ?? '',
    method: request.method,
    data: request.data,
    b2cId,
    companyId: authStore.companyData?.id ?? null,
    savedAt: Date.now(),
  })
}

/**
 * Handles a failed token refresh: tells the user the session expired
 * (toast now, persistent message on the login page after the redirect),
 * then logs out and redirects once the toast had time to be read.
 * The logout is deferred because it triggers a full-page MSAL redirect
 * that would kill any in-app message.
 */
function handleRefreshFailure(request) {
  if (!isHandlingRefreshFailure) {
    isHandlingRefreshFailure = true

    const isMutation = MUTATING_METHODS.has(request?.method?.toLowerCase())
    const reason = isMutation
      ? LOGOUT_REASONS.SESSION_EXPIRED_ACTION_NOT_SAVED
      : LOGOUT_REASONS.SESSION_EXPIRED

    setLogoutReason(reason)
    capturePendingSubmission(request, isMutation)

    ToastEventBus.emit('add', {
      severity: 'error',
      summary: i18n.global.t('errors.refresh-token.session-expired-title'),
      detail: isMutation
        ? i18n.global.t('errors.refresh-token.session-expired-action-not-saved-detail')
        : i18n.global.t('errors.refresh-token.session-expired-detail'),
      life: SESSION_EXPIRED_REDIRECT_DELAY_MS,
    })

    setTimeout(async () => {
      const authStore = useAuthStore()
      // Persisted (not passed as a query param) because the MSAL logout does
      // a full-page round-trip that discards in-memory navigation state.
      // After re-login the login flow honors it and drops the user back
      // where the session died.
      setPostLoginRedirect(router.currentRoute.value.fullPath)

      await authStore.logout()

      if (router.currentRoute.value.name !== 'AuthLogin') {
        router.push({ name: 'AuthLogin' })
      }
    }, SESSION_EXPIRED_REDIRECT_DELAY_MS)
  }

  throw new AuthenticationError(i18n.global.t('errors.refresh-token.authentication-required'))
}

/**
 * Attempts to refresh the token and retry the original request.
 */
async function tryRefreshToken(axiosInstance, request) {
  request._retry = true

  const authStore = useAuthStore()
  if (!authStore.companyData && !sessionStorage.getItem('companyAuth')) {
    throw new AuthenticationError(i18n.global.t('errors.refresh-token.no-active-session'))
  }

  const newTokens = await authStore.refreshTokens()
  if (!newTokens?.data?.accessToken) {
    throw new AuthenticationError(i18n.global.t('errors.refresh-token.failed'))
  }

  request.headers.Authorization = `Bearer ${newTokens.data.accessToken}`
  return axiosInstance(request)
}

/**
 * Installs the interceptor on the provided axios instance.
 */
export function axiosInterceptorRefreshToken(axiosInstance) {
  axiosInstance.interceptors.response.use(
    response => response,
    async (error) => {
      const originalRequest = error.config

      if (!axios.isAxiosError(error)) {
        return Promise.reject(error)
      }

      if (isPublicRequest(originalRequest)) {
        return Promise.reject(error)
      }

      if (isUnauthorizedError(error, originalRequest)) {
        try {
          return await tryRefreshToken(axiosInstance, originalRequest)
        }
        catch {
          return handleRefreshFailure(originalRequest)
        }
      }

      if (
        error.response
        && [400, 403, 404, 429, 500, 501, 502, 503].includes(error.response.status)
      ) {
        if ([500, 501, 502, 503].includes(error.response.status)) {
          error.response.data.error = i18n.global.t('errors.api.500')
          return error.response
        }
        else if ([403].includes(error.response.status)) {
          error.response.data.error = error.response.data.message
          return error.response
        }
        else if ([404].includes(error.response.status)) {
          error.response.data = {}
          error.response.data.error = i18n.global.t('errors.api.404')
          return error.response
        }
        else {
          return error.response || Promise.reject(new ApiError(error.message, error.response.status, error.response))
        }
      }

      return Promise.reject(error)
    },
  )
}
